// lib
import { curry, map, reduce } from "ramda";
// src
import { MY_PLANNING_ID, FETCHABLE_RESOURCES, PresetType, ResourceType } from "config/constants";
import { mdissoc } from "utils/object";
import { plainHash, encodedHash } from "./hash";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const RESOURCE_PREFIX = "?";
const PACK_PREFIX = "~";
const CATEGORY_PREFIX = ">";

export const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "undefined") return [value];
  return [];
};

export const detach = (preset) => {
  const { name, refName } = preset;
  return {
    ...mdissoc(["refName", "underId", "editing"], preset),
    name: refName || name,
  };
};

export const injectIndex = (presets) => presets.map((preset, index) => ({ ...preset, index }));

// Preset id created for a resource always starts with a given prefix
const generatePresetIdForResource = (params) => `${RESOURCE_PREFIX}${plainHash(params)}`;
// Preset id created for a resource always starts with a given prefix
const generatePresetIdForInstant = (params) => `${PACK_PREFIX}${encodedHash(params)}`;

// works on preset (id or params) or query (id or flattened params + dateStart&dateEnd)
export const getPresetId = (query) => {
  // PERMAMENT or already created
  if (query.id) return query.id;
  // INSTANT
  if (query.type === PresetType.INSTANT) return generatePresetIdForInstant(query);
  // RESOURCE
  // removes dates, params can be under params key of flattend in the query sent by the back
  const { params, dateStart, dateEnd, ...flattenedParams } = query;
  return generatePresetIdForResource(params || flattenedParams);
};

export const getRequestId = (query) => {
  const id = getPresetId(query);
  const { dateStart, dateEnd } = query;
  return plainHash({ id, dateStart, dateEnd });
};

export const getResourceType = (qualifier) => {
  for (const resource of FETCHABLE_RESOURCES) {
    if (resource.singular === qualifier) return resource.type;
  }
  return ResourceType.UNKNOWN;
};

export const isPermanentId = (id) => UUID_REGEX.test(id) || id === MY_PLANNING_ID;
export const isInstantId = (id) => id.startsWith(PACK_PREFIX);
export const isCategoryId = (id) => id.startsWith(CATEGORY_PREFIX);

export const getTypeFromId = (id) => {
  if (isPermanentId(id)) return PresetType.PERMANENT;
  if (isInstantId(id)) return PresetType.INSTANT;
  if (isCategoryId(id)) return PresetType.CATEGORY;
  return PresetType.RESOURCE;
};

export const forgePresetFromResource = (resource) => {
  const family = FETCHABLE_RESOURCES.find(({ type }) => type === resource.type);
  const params = {
    [family.plural]: [resource.type === PresetType.RESOURCE ? resource.uuid : resource.id],
  };

  return {
    id: getPresetId(params),
    params,
    share: resource.share,
    resource,
  };
};

export const join = curry((byId, ids) => map((id) => byId[id] || { id }, ids));

export const strictJoin = curry((byId, ids) =>
  reduce(
    (acc, id) => {
      const found = byId[id];
      return found ? [...acc, found] : acc;
    },
    [],
    ids,
  ),
);

// qualify enabled presets which are not categories
export const qualifyEnableds = ({ byId, enabledIds, lightEnabledIds, isLight }) => {
  const ids = isLight ? lightEnabledIds : enabledIds;
  const joined = join(byId, ids);
  return joined.filter(({ type }) => type !== PresetType.CATEGORY);
};
